// Data models and initial state for roadmap data

const getCurrentDate = () => {
  return new Date().toISOString().split('T')[0];
};

export const createInitialRoadmapData = () => ({
  project_type: 'personal',
  project_name: '',
  created_date: getCurrentDate(),
  last_modified: getCurrentDate(),
  completion_status: 0,
  smart_goals: [],
  steps: {
    vision: {
      completed: false,
      vision_statement: '',
      mission_statement: '',
      purpose: '',
      values: [],
      timeframe: '3-5',
      stakeholders: [],
      success_metrics: [],
      inspiration_sources: []
    },
    situation_analysis: {
      completed: false,
      strengths: [],
      weaknesses: [],
      opportunities: [],
      threats: [],
      environmental_factors: []
    },
    strategic_objectives: {
      completed: false,
      objectives: []
    },
    action_components: {
      completed: false,
      components: []
    },
    resources: {
      completed: false,
      human_resources: [],
      financial_resources: [],
      technology_resources: [],
      other_resources: []
    },
    timeline: {
      completed: false,
      start_date: '',
      end_date: '',
      phases: []
    },
    risks: {
      completed: false,
      risk_items: []
    },
    governance: {
      completed: false,
      decision_makers: [],
      communication_plan: '',
      responsibility_matrix: {}
    },
    performance: {
      completed: false,
      kpis: [],
      review_schedule: ''
    },
    adaptation: {
      completed: false,
      reassessment_points: [],
      pivot_guidelines: '',
      lessons_learned: []
    }
  }
});

// Validation functions
export const validateStep = (stepName, stepData) => {
  switch (stepName) {
    case 'vision':
      return stepData.vision_statement && stepData.purpose;
    case 'situation_analysis':
      return stepData.strengths.length > 0 && stepData.weaknesses.length > 0;
    case 'strategic_objectives':
      return stepData.objectives.length > 0;
    case 'action_components':
      return stepData.components.length > 0;
    case 'resources':
      return stepData.human_resources.length > 0 || 
             stepData.financial_resources.length > 0 || 
             stepData.technology_resources.length > 0;
    case 'timeline':
      return stepData.start_date && stepData.end_date;
    case 'risks':
      return stepData.risk_items.length > 0;
    case 'governance':
      return stepData.decision_makers.length > 0;
    case 'performance':
      return stepData.kpis.length > 0;
    case 'adaptation':
      return stepData.reassessment_points.length > 0;
    default:
      return false;
  }
};

export const formatDateForInput = (date) => {
  if (!date) return '';
  if (typeof date === 'string') return date;
  return date.toISOString().split('T')[0];
};

export const parseDate = (dateString) => {
  if (!dateString) return null;
  return new Date(dateString);
};
// ── SMART goals ──────────────────────────────────────────────────────────────
// Kept alongside the 10 roadmap steps rather than inside them, so goals never
// affect step completion or the progress bar.

const randomId = () =>
  `g${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;

export const createEmptySmartGoal = (overrides = {}) => ({
  id: randomId(),
  title: '',
  category: 'business',
  specific: '',
  measurable: { metric: '', baseline: '', target: '', current: '', unit: '' },
  achievable: '',
  relevant: '',
  time_bound: { start_date: getCurrentDate(), due_date: '', review_frequency: 'weekly' },
  owner: '',
  priority: 'Medium',
  status: 'not-started',
  progress: 0,
  milestones: [],
  obstacles: [],
  checkins: [],
  linked_objective: null,
  created_date: getCurrentDate(),
  last_modified: getCurrentDate(),
  ...overrides
});

// Normalize anything (AI output, an older saved goal) into a full goal object
export const normalizeSmartGoal = (raw = {}) => {
  const base = createEmptySmartGoal();
  return {
    ...base,
    ...raw,
    id: raw.id || base.id,
    measurable: { ...base.measurable, ...(raw.measurable || {}) },
    time_bound: { ...base.time_bound, ...(raw.time_bound || {}) },
    milestones: Array.isArray(raw.milestones) ? raw.milestones : [],
    obstacles: Array.isArray(raw.obstacles) ? raw.obstacles : [],
    checkins: Array.isArray(raw.checkins) ? raw.checkins : []
  };
};
